
/**
 * Meeting Integration Layer
 * Connects SDK Service to Zustand Store with tenant context
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useMeetingStore } from '../../store/meeting.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
export class MeetingIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch all items and update store
     */
    async fetchMeetings(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useMeetingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setMeetings(data || []);

            // Map Pagination
            if (pagination) {
                store.setPagination({
                    currentPage: pagination.page,
                    itemsPerPage: pagination.pageSize,
                    totalItems: pagination.total,
                    totalPages: pagination.totalPages,
                });
            } else {
                store.setPagination({
                    currentPage: page,
                    itemsPerPage: pageSize,
                    totalItems: data?.length || 0,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchMeetingById(id) {
        const store = useMeetingStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setSelectedMeeting(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchReviewInputs(meetingId) {
        try {
            const res = await this.service.getReviewInputs(meetingId);
            return { success: true, data: res.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async uploadYggReport(file, optionsOrCallback) {
        try {
            const response = await this.service.uploadYggReport(file, optionsOrCallback);
            toast.success(
                i18next.t('iso27001Meeting:messages.yggReportUploadSuccess', {
                    defaultValue: 'YGG report uploaded',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async discardUploadedYggReport(body) {
        try {
            await this.service.discardUploadedYggReport(body);
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            // Linked files return 409 — leave them for meeting update cleanup.
            if (error?.code === 'YGG_REPORT_IN_USE' || error?.response?.data?.code === 'YGG_REPORT_IN_USE') {
                return { success: true, skipped: true };
            }
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Create item
     */
    async createMeeting(data) {
        const store = useMeetingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('iso27001Meeting:messages.createSuccess', { defaultValue: 'Meeting created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateMeeting(id, data) {
        const store = useMeetingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('iso27001Meeting:messages.updateSuccess', { defaultValue: 'Meeting updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteMeeting(id) {
        const store = useMeetingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            await this.service.delete(id);
            toast.success(i18next.t('iso27001Meeting:messages.deleteSuccess', { defaultValue: 'Meeting deleted' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete items
     */
    async batchDeleteMeeting(ids) {
        const store = useMeetingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('iso27001Meeting:messages.bulkDeleteSuccess', { count: ids.length, defaultValue: `${ids.length} items deleted` }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
